import React from "react";
import { Mail, Phone, MapPin, Clock, MessageCircle, Building2 } from "lucide-react";
import { COMPANY, fullAddress } from "../lib/company";
import { useT } from "../lib/i18n";
import SEO from "../components/SEO";

export default function Contact() {
  const { t } = useT();
  return (
    <div data-testid="contact-page">
      <SEO
        title={t("contact.title")}
        description={t("contact.sub")}
        path="/contact"
      />

      <section className="border-b border-gray-200 bg-silver-shimmer">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-gray-500">{t("contact.eye")}</p>
          <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter mt-3">{t("contact.title")}</h1>
          <p className="mt-4 text-gray-600 max-w-xl">{t("contact.sub")}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
          {[
            { Icon: Mail,           t: t("contact.email"),    v: COMPANY.contact.email,         href: `mailto:${COMPANY.contact.email}`,     hint: t("contact.email.hint") },
            { Icon: Phone,          t: t("contact.phone"),    v: COMPANY.contact.phone,         href: `tel:${COMPANY.contact.phone_tel}`,    hint: t("contact.phone.hint") },
            { Icon: MessageCircle,  t: t("contact.whatsapp"), v: t("contact.whatsapp.value"),   href: `https://wa.me/${COMPANY.contact.whatsapp}`, hint: t("contact.whatsapp.hint"), external: true, accent: "text-[#1ebe57] border-[#1ebe57]" },
            { Icon: Clock,          t: t("contact.hours"),    v: t("contact.hours.value"),      href: null,                                   hint: t("contact.hours.hint") },
          ].map((c, i) => (
            <div key={i} className={`border p-6 hover:bg-gray-50 transition ${c.accent || 'border-gray-300'}`}>
              <c.Icon size={22} strokeWidth={1.5} className={c.accent || "text-gray-900"} />
              <p className="mt-4 text-[10px] uppercase tracking-[0.25em] text-gray-500">{c.t}</p>
              {c.href ? (
                <a href={c.href} target={c.external ? "_blank" : undefined} rel={c.external ? "noopener noreferrer" : undefined}
                  className="font-display text-xl font-bold mt-2 block hover:text-gray-700" data-testid={`contact-${c.t.toLowerCase()}`}>
                  {c.v}
                </a>
              ) : (
                <p className="font-display text-xl font-bold mt-2">{c.v}</p>
              )}
              <p className="text-xs text-gray-500 mt-2">{c.hint}</p>
            </div>
          ))}
          <div className="sm:col-span-2 border border-gray-300 p-6">
            <MapPin size={22} strokeWidth={1.5} />
            <p className="mt-4 text-[10px] uppercase tracking-[0.25em] text-gray-500">{t("contact.hq")}</p>
            <p className="font-semibold mt-2">{COMPANY.address.street}</p>
            <p className="text-sm text-gray-700">{COMPANY.address.city}, {COMPANY.address.county}, {COMPANY.address.country}</p>
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="border border-gray-200 bg-gray-50/60 p-6 md:p-8">
            <Building2 size={26} strokeWidth={1.5} />
            <h2 className="font-display text-2xl font-black tracking-tight mt-4 uppercase">{t("contact.company")}</h2>
            <dl className="mt-6 divide-y divide-gray-200">
              {[
                [t("contact.label.name"), COMPANY.legal_name],
                [t("contact.label.cui"),  COMPANY.cif],
                [t("contact.label.reg"),  COMPANY.reg_com],
                [t("contact.label.hq"),   fullAddress()],
                [t("contact.label.vat"),  COMPANY.vat_payer ? `${t("contact.vat.payer")} (${COMPANY.vat_rate}%)` : t("contact.vat.nonpayer")],
              ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 py-3 gap-3 text-sm">
                  <dt className="text-gray-500">{k}</dt>
                  <dd className="col-span-2 font-medium text-gray-900 break-words">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </aside>
      </section>
    </div>
  );
}
